import { createAsyncThunk } from "@reduxjs/toolkit";
import { chatApi } from "../../services/api";
import { getApiUrl } from "../../config";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

export const getDirectContact = createAsyncThunk("chat/getDirectContact", async () => {
  try {
    const data = await chatApi.getRooms();

    if (data.success && data.rooms) {
      // Transform rooms to match the expected format for Chat page
      const directContacts = data.rooms
        .filter((room: any) => room.roomType === 'direct' || room.roomType === 'support')
        .map((room: any) => ({
          id: room.id,
          roomId: room.roomId,
          name: room.name || room.otherUser?.name || "Support Chat",
          userName: room.otherUser?.fullName || room.otherUser?.name || null, // User's display name (prefer fullName)
          image: room.otherUser?.avatar ? getApiUrl(room.otherUser.avatar) : null,
          status: room.otherUser?.status || "Online",
          badge: room.unreadCount || 0,
          bgColor: room.status === "new" ? "danger" : (room.status === "waiting_reply" ? "warning" : "primary"),
          number: "",
          email: room.otherUser?.email || "",
          location: "",
          // New fields for status display
          chatStatus: room.status || "open",
          adminReplied: room.adminReplied || false,
          lastSenderIsUser: room.lastSenderIsUser !== false,
          lastSenderType: room.lastSenderIsUser !== false ? "user" : "admin",
          lastMessage: room.lastMessage || "",
          lastMessageAt: room.lastMessageAt || null,
          updatedAt: room.lastMessageAt || room.createdAt || null,
          // Room status and rating
          roomStatus: room.status || "open",
          rating: room.rating || null
        }));

      const channels = data.rooms
        .filter((room: any) => room.roomType === 'group')
        .map((room: any) => ({
          id: room.id,
          roomId: room.roomId,
          name: room.name,
          image: null,
          unReadMessage: room.unreadCount || 0,
          isChannelsList: true,
          status: null
        }));

      // Return in the format expected by Chat page
      return [
        { direactContact: directContacts },
        { channelsList: channels }
      ];
    }
    return [];
  } catch (error) {
    console.error("Error fetching contacts:", error);
    return [];
  }
});

export const getChannels = createAsyncThunk("chat/getChannels", async () => {
  try {
    const data = await chatApi.getRooms();
    if (data.success) {
      // Filter group chats only
      return data.rooms.filter((room: any) => room.roomType === "group").map((room: any) => ({
        id: room.id,
        roomId: room.roomId,
        name: room.name,
        image: null,
        unreadCount: room.unreadCount || 0
      }));
    }
    return [];
  } catch (error) {
    console.error("Error fetching channels:", error);
    return [];
  }
});

export const getMessages = createAsyncThunk("chat/getMessages", async (roomId: any) => {
  try {
    const data = await chatApi.getRoomMessages(roomId);

    if (data.success && data.messages) {
      // Transform messages to match expected format
      const userMessages = data.messages.map((msg: any, index: number) => ({
        id: msg.id || index + 1,
        from_id: msg.isMine ? 1 : 2,
        to_id: msg.isMine ? 2 : 1,
        msg: msg.message,
        isImages: msg.messageType === 'image',
        has_images: msg.fileUrl && msg.messageType === 'image' ? [{ id: msg.id, image: `${API_URL}${msg.fileUrl}` }] : [],
        datetime: new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        reply: null,
        // File info
        fileUrl: msg.fileUrl || null,
        fileName: msg.fileName || null,
        messageType: msg.messageType || 'text',
        senderType: msg.senderType || 'user',
        senderName: msg.senderName || 'Unknown'
      }));

      // Get sender name from first non-mine message or use default
      const otherMessage = data.messages.find((m: any) => !m.isMine);
      const senderName = otherMessage?.senderName || "Support";

      // Return in expected format
      return [{
        id: 1,
        roomId: roomId,
        sender: senderName,
        createdAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        usermessages: userMessages
      }];
    }
    return [];
  } catch (error) {
    console.error("Error fetching messages:", error);
    return [];
  }
});

export const addMessage = createAsyncThunk("chat/addMessage", async (message: any) => {
  try {
    const data = await chatApi.sendMessage(message.roomId, {
      roomId: message.roomId,
      message: message.msg || message.message,
      messageType: "text"
    });

    if (data.success) {
      // Return in the format expected by the reducer
      return {
        id: data.message.id,
        from_id: 1, // Current user
        to_id: 2,
        msg: data.message.message,
        isImages: false,
        has_images: [],
        datetime: new Date(data.message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        reply: message.reply || null
      };
    }

    // Fallback - return the message as sent
    return {
      id: Math.floor(Math.random() * 10000),
      from_id: 1,
      to_id: 2,
      msg: message.msg || message.message,
      isImages: false,
      has_images: [],
      datetime: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      reply: message.reply || null
    };
  } catch (error) {
    console.error("Error sending message:", error);
    // Return message anyway for optimistic update
    return {
      id: Math.floor(Math.random() * 10000),
      from_id: 1,
      to_id: 2,
      msg: message.msg || message.message,
      isImages: false,
      has_images: [],
      datetime: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      reply: message.reply || null
    };
  }
});

export const deleteMessage = createAsyncThunk("chat/deleteMessage", async (messageId: any) => {
  // For now, just return the message ID to remove from state
  // Backend delete can be implemented later
  return messageId;
});

export const getChatContacts = createAsyncThunk("chat/getChatContacts", async () => {
  try {
    const data = await chatApi.getChatUsers();

    if (data.success && data.contacts) {
      return data.contacts;
    }
    return [];
  } catch (error) {
    console.error("Error fetching contacts:", error);
    return [];
  }
});
